// Diagnoses why edges don't render in the dynamic-node mirror.
//
// For every edge it reports the handle the edge declares, the handle the dynamic view's
// catalog normaliser applies, and the handles the node actually renders (via `renderedHandles`).
//
// A "rewritten" edge is the subtle case: its declared handle matched nothing, so the editor
// silently moved it to the node's first handle — it draws, but on the wrong port.

import { normalizeGraphHandles } from "./graphHandles";
import { normalizeGraphHandlesFromDefinitions, renderedHandles } from "./dynamicNodeData";

const idOf = (value) => (value === undefined ? null : value);

const handleIds = (handles) => handles.map((handle) => idOf(handle.id));

const quoted = (value) => JSON.stringify(idOf(value));

/**
 * @param {{nodes?: object[], edges?: object[]}} graph
 * @param {Record<string, object>} definitionsByType Normalised node definitions, keyed by type.
 * @returns {object[]} One row per edge.
 */
export const diagnoseGraphEdges = (graph = {}, definitionsByType = {}) => {
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  const byId = new Map(nodes.map((node) => [String(node.id), node]));

  // The dynamic view maps handles from the catalog, not the static per-type map, so diagnose the
  // same way — otherwise every catalog-renamed handle looks like a broken edge.
  const normalized = Object.keys(definitionsByType ?? {}).length > 0
    ? normalizeGraphHandlesFromDefinitions({ nodes, edges }, definitionsByType)
    : normalizeGraphHandles({ nodes, edges });
  const normalizedById = new Map(normalized.edges.map((edge) => [String(edge.id), edge]));

  return edges.map((edge) => {
    const applied = normalizedById.get(String(edge.id)) ?? edge;
    const sourceNode = byId.get(String(edge.source));
    const targetNode = byId.get(String(edge.target));
    const sourceDefinition = sourceNode ? definitionsByType[sourceNode.type] : null;
    const targetDefinition = targetNode ? definitionsByType[targetNode.type] : null;

    const sourceHandles = sourceDefinition
      ? handleIds(renderedHandles(sourceDefinition, sourceNode.data ?? {}).sources)
      : null;
    const targetHandles = targetDefinition
      ? handleIds(renderedHandles(targetDefinition, targetNode.data ?? {}).targets)
      : null;

    const declaredSource = idOf(edge.sourceHandle);
    const declaredTarget = idOf(edge.targetHandle);
    const appliedSource = idOf(applied.sourceHandle);
    const appliedTarget = idOf(applied.targetHandle);

    const declaredSourceOk = sourceHandles ? sourceHandles.includes(declaredSource) : false;
    const declaredTargetOk = targetHandles ? targetHandles.includes(declaredTarget) : false;
    const sourceOk = sourceHandles ? sourceHandles.includes(appliedSource) : false;
    const targetOk = targetHandles ? targetHandles.includes(appliedTarget) : false;

    const rewritten = declaredSource !== appliedSource || declaredTarget !== appliedTarget;

    const problems = [];
    if (!sourceNode) problems.push("source node missing");
    if (!targetNode) problems.push("target node missing");
    if (sourceNode && !sourceDefinition) problems.push(`no definition for ${sourceNode.type}`);
    if (targetNode && !targetDefinition) problems.push(`no definition for ${targetNode.type}`);

    if (sourceDefinition && !declaredSourceOk) {
      problems.push(
        `declared source handle ${quoted(declaredSource)} not rendered; normalised to ${quoted(appliedSource)} (available: [${sourceHandles.join(", ")}])`
      );
    }
    if (targetDefinition && !declaredTargetOk) {
      problems.push(
        `declared target handle ${quoted(declaredTarget)} not rendered; normalised to ${quoted(appliedTarget)} (available: [${targetHandles.join(", ")}])`
      );
    }
    if (sourceDefinition && !sourceOk) {
      problems.push(`source handle ${quoted(appliedSource)} does not exist on the node`);
    }
    if (targetDefinition && !targetOk) {
      problems.push(`target handle ${quoted(appliedTarget)} does not exist on the node`);
    }

    return {
      id: edge.id,
      source: edge.source,
      sourceType: sourceNode?.type ?? null,
      target: edge.target,
      targetType: targetNode?.type ?? null,
      declaredSourceHandle: declaredSource,
      declaredTargetHandle: declaredTarget,
      appliedSourceHandle: appliedSource,
      appliedTargetHandle: appliedTarget,
      sourceHandles,
      targetHandles,
      rewritten,
      // `ok` means the edge draws; `rewritten` means it draws on a handle it didn't ask for.
      ok: sourceOk && targetOk && Boolean(sourceDefinition) && Boolean(targetDefinition),
      problems,
    };
  });
};

/**
 * A short, copy-pasteable report of the edges that draw wrong or not at all.
 *
 * @param {object[]} diagnostics
 */
export const summarizeEdgeDiagnostics = (diagnostics = []) => {
  const failing = diagnostics.filter((row) => !row.ok || row.rewritten);

  return {
    total: diagnostics.length,
    failing: failing.length,
    edges: failing.map((row) => ({
      id: row.id,
      from: `${row.sourceType ?? "?"} (${row.source})`,
      to: `${row.targetType ?? "?"} (${row.target})`,
      declaredSourceHandle: row.declaredSourceHandle,
      declaredTargetHandle: row.declaredTargetHandle,
      appliedSourceHandle: row.appliedSourceHandle,
      appliedTargetHandle: row.appliedTargetHandle,
      sourceHandles: row.sourceHandles,
      targetHandles: row.targetHandles,
      rewritten: row.rewritten,
      draws: row.ok,
      problems: row.problems,
    })),
  };
};

export default diagnoseGraphEdges;
